import {
  ComponentType,
  CSSProperties,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { gsap } from "gsap";
import type { LucideIcon } from "lucide-react";
import {
  ArrowDownRight,
  ArrowUpRight,
  BellRing,
  CircleCheck,
  Radio,
  Webhook,
} from "lucide-react";
import {
  monotoneLinePath as linePath,
  monotonePoints as smoothPoints,
  type Pt,
} from "features/onboarding/art/chart";
import { Art, ArtSwitch, Card, Chip } from "features/onboarding/art/live";
import {
  TXT,
  follow,
  font,
  motionAllowed,
  r2,
  soft,
  tone,
  useArtId,
  type Box,
  type Tone,
} from "features/onboarding/art/kit";
import useArtLabels, { type ArtLabels } from "features/onboarding/art/labels";
import { TradingIntent } from "features/onboarding/guided/intent";

/*
 * The walkthrough ("How this method works") scene for each method, drawn in
 * the cornix.io illustration style and driven by the example's event
 * (`stage`). It is deliberately quiet: one bot window, at most six order
 * levels whose status is a small mark (filled = solid with a check, pending =
 * hollow, the live stop = an orange ring), and a brief toast for the event.
 * Entries are green, targets red and the stop orange. Labels and prices have
 * their own lane left of the plot, so no line ever crosses them.
 *
 * Two layouts: `wide` (desktop panel) and `compact` (~16:10, phones).
 * Reduced motion and e2e runs show every state at rest.
 */

/** Seconds the price takes to reach an event's fill. */
const MOVE = 0.9;
const ARRIVE = MOVE * 0.85;

type Variant = {
  name: "w" | "c";
  bar: number;
  mark: number;
  label: number;
  price: number;
  title: number;
  toastH: number;
};
const WIDE: Variant = {
  name: "w",
  bar: 32,
  mark: 6.5,
  label: 11,
  price: 12,
  title: 11.5,
  toastH: 32,
};
const COMPACT: Variant = {
  name: "c",
  bar: 26,
  mark: 5.5,
  label: 10.5,
  price: 11,
  title: 10.5,
  toastH: 28,
};

const delay = (s: number) => ({ "--ak-delay": `${s}s` } as CSSProperties);

/** Mounts with a short rise (after `after` seconds); static with reduced motion. */
const Appear = ({
  after = 0,
  children,
}: {
  after?: number;
  children: ReactNode;
}) => (
  <g className="ak-rise" style={delay(after)}>
    {children}
  </g>
);

/**
 * Price line drawn up to `f` (0–1 of its length) with the live head at its
 * end. A larger `f` eases forward over MOVE seconds; a smaller one (replay)
 * jumps back. Returns the line and the head separately, so the scene can draw
 * the line under its levels and the head on top.
 */
function useStepPrice(pts: Pt[], f: number, r = 5) {
  const lineRef = useRef<SVGPathElement>(null);
  const headRef = useRef<SVGGElement>(null);
  const shown = useRef(f);
  const path = useMemo(() => follow(pts), [pts]);
  const d = useMemo(() => linePath(pts), [pts]);
  // Only the first render sets these attributes; the effect owns them after,
  // so a re-render never snaps the line back mid-move.
  const [initial] = useState(() => ({ off: 1 - f, at: path.at(f) }));
  useLayoutEffect(() => {
    const length = lineRef.current?.getTotalLength() ?? 0;
    const apply = (p: number) => {
      lineRef.current?.setAttribute("stroke-dashoffset", (1 - p).toFixed(4));
      const point = lineRef.current?.getPointAtLength(p * length);
      const [x, y] = point ? [point.x, point.y] : path.at(p);
      headRef.current?.setAttribute(
        "transform",
        `translate(${x.toFixed(3)} ${y.toFixed(3)})`
      );
    };
    const from = shown.current;
    if (f <= from || !motionAllowed()) {
      shown.current = f;
      apply(f);
      return;
    }
    const o = { p: from };
    const tween = gsap.to(o, {
      p: f,
      duration: MOVE,
      ease: "power1.inOut",
      onUpdate: () => {
        shown.current = o.p;
        apply(o.p);
      },
    });
    return () => {
      tween.kill();
    };
  }, [f, path]);
  const line = (
    <path
      ref={lineRef}
      d={d}
      pathLength={1}
      fill="none"
      stroke={tone("accent")}
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray="1 1"
      strokeDashoffset={initial.off.toFixed(4)}
    />
  );
  const head = (
    <g
      ref={headRef}
      transform={`translate(${initial.at[0].toFixed(1)} ${initial.at[1].toFixed(
        1
      )})`}
    >
      <circle r={r * 2} fill={tone("accent")} opacity={0.2} />
      <circle
        r={r}
        fill="var(--viz-card)"
        stroke={tone("accent")}
        strokeWidth={2.4}
      />
    </g>
  );
  return { line, head };
}

/** Points of a smooth line through keys, and each key's length fraction. */
const keyed = (keys: Pt[], steps = 10) => {
  const pts = smoothPoints(keys, steps).map(([x, y]): Pt => [r2(x), r2(y)]);
  const { fractions } = follow(pts);
  return { pts, at: (k: number) => fractions[k * steps], point: keys };
};

/* ------------------------------------------------------------------------ */
/* Shapes                                                                   */
/* ------------------------------------------------------------------------ */

type MarkState = "filled" | "pending" | "active";

/**
 * An order's status: solid with a check once filled, hollow while pending, an
 * orange ring for the live stop. The status word is its title (read by
 * assistive technology, and after the level's label and price).
 */
function Mark({
  x,
  y,
  r,
  t,
  state,
  title,
  fresh,
}: {
  x: number;
  y: number;
  r: number;
  t: Tone;
  state: MarkState;
  title: string;
  fresh?: boolean;
}) {
  const body =
    state === "filled" ? (
      <>
        <circle cx={x} cy={y} r={r} fill={tone(t)} />
        <path
          d={`M${r2(x - r * 0.45)},${r2(y + r * 0.02)} l${r2(r * 0.32)},${r2(
            r * 0.32
          )} l${r2(r * 0.58)},${r2(-r * 0.62)}`}
          fill="none"
          stroke="var(--viz-on-solid)"
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ) : state === "active" ? (
      <>
        <circle cx={x} cy={y} r={r * 1.7} fill={soft("gold")} />
        <circle
          cx={x}
          cy={y}
          r={r - 0.6}
          fill="var(--viz-card)"
          stroke={tone("gold")}
          strokeWidth={2.2}
        />
      </>
    ) : (
      <circle
        cx={x}
        cy={y}
        r={r - 0.8}
        fill="var(--viz-card)"
        stroke={tone(t)}
        strokeOpacity={0.75}
        strokeWidth={1.5}
      />
    );
  return (
    <g
      className={fresh ? "ak-pop" : undefined}
      style={fresh ? delay(ARRIVE) : undefined}
    >
      <title>{title}</title>
      {body}
    </g>
  );
}

type Level = {
  /** The level's word in the lane; empty when its price says it in colour. */
  label: string;
  price: string;
  /** Spoken name of an unlabelled level ("Sell order"). */
  name?: string;
  y: number;
  t: Tone;
  state: MarkState;
  status: string;
};

type Lane = {
  /** Label column. */
  x1: number;
  /** Price column. */
  priceX: number;
  /** Plot: level lines and the price line. */
  lineX: number;
  x2: number;
  /** Marks, right of the plot. */
  markX: number;
};

/** The label lane and plot for a window, sized to its words. */
const lane = (
  win: Box,
  v: Variant,
  labels: string[],
  prices: string[]
): Lane => {
  const x1 = win.x + (v.name === "w" ? 18 : 12);
  // Without labels the price leads the lane (coloured by its side).
  const priceX = labels.length
    ? r2(x1 + Math.max(...labels.map((l) => l.length)) * v.label * 0.56 + 8)
    : x1;
  const lineX = r2(
    priceX + Math.max(...prices.map((p) => p.length)) * v.price * 0.6 + 12
  );
  const markX = win.x + win.w - (v.name === "w" ? 18 : 12) - v.mark;
  return { x1, priceX, lineX, x2: markX - v.mark - 10, markX };
};

/**
 * One order level: label and price in the lane, its line, its mark. When an
 * order fills with the current event, its pending look stays until the price
 * arrives, then the filled line and mark fade/pop in over it.
 */
function LevelRow({
  l,
  c,
  v,
  fresh,
}: {
  l: Level;
  c: Lane;
  v: Variant;
  fresh: boolean;
}) {
  const filled = l.state === "filled";
  const line = (solid: boolean) => (
    <line
      x1={c.lineX}
      x2={c.x2}
      y1={l.y}
      y2={l.y}
      stroke={tone(l.t)}
      strokeOpacity={solid ? 0.85 : 0.5}
      strokeWidth={1.2}
      strokeDasharray={solid ? undefined : "4 4"}
    />
  );
  const mark = (state: MarkState, pop: boolean) => (
    <Mark
      key={state}
      x={c.markX}
      y={l.y}
      r={v.mark}
      t={l.t}
      state={state}
      title={l.status}
      fresh={pop}
    />
  );
  return (
    <g>
      {l.label ? (
        <text
          x={c.x1}
          y={l.y + 3.6}
          className={TXT.ink3}
          style={font(v.label, 400)}
        >
          {l.label}
        </text>
      ) : null}
      <text
        x={c.priceX}
        y={l.y + 3.8}
        className={l.label ? `${TXT.ink} ${TXT.num}` : TXT.num}
        fill={l.label ? undefined : tone(l.t)}
        style={font(v.price, l.label ? 500 : 600)}
      >
        {l.name ? <title>{`${l.name} ${l.price}`}</title> : null}
        {l.price}
      </text>
      {filled && fresh ? (
        <>
          {line(false)}
          <g className="ak-fade" style={delay(ARRIVE)}>
            {line(true)}
          </g>
          <g aria-hidden>{mark("pending", false)}</g>
          {mark("filled", true)}
        </>
      ) : (
        <>
          {line(filled)}
          {mark(l.state, false)}
        </>
      )}
    </g>
  );
}

/** A small dot on the price line where an order filled. */
function Fill({ at, t, fresh }: { at: Pt; t: Tone; fresh: boolean }) {
  return (
    <circle
      className={fresh ? "ak-pop" : undefined}
      style={fresh ? delay(ARRIVE) : undefined}
      cx={at[0]}
      cy={at[1]}
      r={4}
      fill={tone(t)}
      stroke="var(--viz-card)"
      strokeWidth={1.5}
    />
  );
}

/** The bot's window: a card with a slim title bar. */
function Frame({
  id,
  b,
  v,
  title,
  aside,
  children,
}: {
  id: string;
  b: Box;
  v: Variant;
  title: string;
  aside?: string;
  children?: ReactNode;
}) {
  const cy = b.y + v.bar / 2;
  return (
    <g>
      <rect
        x={b.x}
        y={b.y}
        width={b.w}
        height={b.h}
        rx={v.name === "w" ? 12 : 10}
        fill="var(--viz-card)"
        stroke="var(--viz-card-line)"
        filter={`url(#${id}-shadow)`}
      />
      <line
        x1={b.x}
        x2={b.x + b.w}
        y1={b.y + v.bar}
        y2={b.y + v.bar}
        stroke="var(--viz-card-line)"
      />
      {[0, 1, 2].map((i) => (
        <circle
          key={i}
          cx={b.x + 16 + i * 11}
          cy={cy}
          r={3.2}
          fill="var(--viz-ink-soft)"
          stroke="var(--viz-card-line)"
        />
      ))}
      <text
        x={b.x + 56}
        y={cy + 4}
        className={`${TXT.ink2} ${TXT.num}`}
        style={font(v.title, 500)}
      >
        {title}
      </text>
      {aside ? (
        <text
          x={b.x + b.w - 14}
          y={cy + 4}
          textAnchor="end"
          className={`${TXT.ink3} ${TXT.num}`}
          style={font(v.title - 0.5, 400)}
        >
          {aside}
        </text>
      ) : null}
      {children}
    </g>
  );
}

type ToastSpec = { icon: LucideIcon; t: Tone; title: string };

/** Gap between the toast and the window's bottom edge. */
const toastInset = (v: Variant) => (v.name === "w" ? 12 : 8);
/** Room a window keeps free at its bottom for the event toast. */
const toastRoom = (v: Variant) => v.toastH + toastInset(v);

/**
 * The event, briefly: a small toast centered at the bottom of the window,
 * fully inside it and clear of every level. It fades after a moment; the
 * steps list keeps the story. Its text stays in the drawing for assistive
 * technology.
 */
function BriefToast({
  id,
  spec,
  stage,
  win,
  v,
}: {
  id: string;
  spec?: ToastSpec;
  stage: number;
  win: Box;
  v: Variant;
}) {
  if (!spec) return null;
  const h = v.toastH;
  const w = Math.round(spec.title.length * (v.name === "w" ? 6.6 : 6) + h + 20);
  const x = r2(win.x + win.w / 2 - w / 2);
  const y = r2(win.y + win.h - toastInset(v) - h);
  const Icon = spec.icon;
  const icon = h - 12;
  return (
    <g key={stage} className="ak-brief" style={delay(ARRIVE)}>
      <Card id={id} x={x} y={y} w={w} h={h} r={h / 2}>
        <circle
          cx={x + h / 2}
          cy={y + h / 2}
          r={icon / 2}
          fill={soft(spec.t)}
        />
        <Icon
          x={x + h / 2 - icon * 0.32}
          y={y + h / 2 - icon * 0.32}
          width={icon * 0.64}
          height={icon * 0.64}
          color={tone(spec.t)}
          strokeWidth={2.2}
        />
        <text
          x={x + h + 4}
          y={y + h / 2 + 4}
          className={TXT.ink}
          style={font(v.name === "w" ? 12 : 11, 600)}
        >
          {spec.title}
        </text>
      </Card>
    </g>
  );
}

type SceneProps = {
  v: Variant;
  stage: number;
  total: number;
  L: ArtLabels;
  label: string;
};

const eventLabel = (L: ArtLabels, stage: number, total: number) =>
  stage === 0 ? L.sampleSetup : L.eventCount(stage, total);

const bandX = (x1: number, x2: number, u: number) =>
  r2(x1 + 6 + u * (x2 - x1 - 12));

/* ------------------------------------------------------------------------ */
/* Signals                                                                  */
/* ------------------------------------------------------------------------ */

// Top to bottom: targets, entries, stop-loss (rows are evenly spaced).
const SIGNAL_PRICES = [64000, 62000, 60000, 59000, 58000, 56000];
const signalRow = (p: number) => {
  for (let i = 1; i < SIGNAL_PRICES.length; i++) {
    const a = SIGNAL_PRICES[i - 1];
    const b = SIGNAL_PRICES[i];
    if (p >= b) return i - 1 + (a - p) / (a - b);
  }
  return SIGNAL_PRICES.length - 1;
};
// A calm path of explicit key points: start, a small high, each fill exactly
// on its level, one low, and the target. Events end on keys 1 (sample), 2
// (entry 1), 3 (entry 2) and 5 (target 1).
const SIGNAL_KEYS: [number, number][] = [
  [0, 60900],
  [0.22, 61300],
  [0.46, 60000],
  [0.64, 59000],
  [0.74, 58600],
  [0.98, 62000],
];
const SIGNAL_STOPS = [1, 1, 2, 3, 5];

type SignalGeo = {
  w: number;
  h: number;
  card: Box;
  win: Box;
  top: number;
  rowH: number;
};
const SIGNAL_GEO: Record<"w" | "c", SignalGeo> = {
  w: {
    w: 560,
    h: 382,
    card: { x: 10, y: 6, w: 540, h: 58 },
    win: { x: 10, y: 76, w: 540, h: 298 },
    top: 136,
    rowH: 36,
  },
  c: {
    w: 360,
    h: 234,
    card: { x: 4, y: 4, w: 352, h: 30 },
    win: { x: 4, y: 42, w: 352, h: 184 },
    top: 80,
    rowH: 20,
  },
};

/** The signal as posted: one line, plus its orders on a second (wide). */
function SignalCard({
  id,
  b,
  v,
  L,
  stage,
}: {
  id: string;
  b: Box;
  v: Variant;
  L: ArtLabels;
  stage: number;
}) {
  const wide = v.name === "w";
  const longW = Math.max(42, Math.round(L.long.length * 6.4 + 18));
  const r = wide ? 11 : 9;
  const cy = wide ? b.y + 20 : b.y + b.h / 2;
  return (
    <Card id={id} x={b.x} y={b.y} w={b.w} h={b.h} r={wide ? 14 : 10}>
      {stage === 1 ? (
        <Appear key="pulse">
          <rect
            x={b.x}
            y={b.y}
            width={b.w}
            height={b.h}
            rx={wide ? 14 : 10}
            fill="none"
            stroke={tone("accent")}
            strokeWidth={1.8}
          />
        </Appear>
      ) : null}
      <circle cx={b.x + 12 + r} cy={cy} r={r} fill={tone("ink")} />
      <Radio
        x={b.x + 12 + r - r * 0.6}
        y={cy - r * 0.6}
        width={r * 1.2}
        height={r * 1.2}
        color="var(--viz-card)"
        strokeWidth={2}
      />
      <text
        x={b.x + 20 + r * 2}
        y={cy + 4}
        className={`${TXT.ink} ${TXT.num}`}
        style={font(wide ? 12.5 : 11, 600)}
      >
        Telegram · BTC/USDT
      </text>
      <Chip
        x={b.x + b.w - (cy - b.y - 10) - longW / 2}
        y={cy}
        text={L.long}
        t="up"
        w={longW}
        sm
      />
      {wide ? (
        <text
          x={b.x + 20 + r * 2}
          y={b.y + 44}
          className={`${TXT.ink3} ${TXT.num}`}
          style={font(11, 400)}
        >
          {L.entry}
          <tspan className={TXT.ink2} dx={6}>
            60,000 · 59,000 · 58,000
          </tspan>
          <tspan dx={18}>TP</tspan>
          <tspan className={TXT.ink2} dx={6}>
            62,000 · 64,000
          </tspan>
          <tspan dx={18}>SL</tspan>
          <tspan className={TXT.ink2} dx={6}>
            56,000
          </tspan>
        </text>
      ) : null}
    </Card>
  );
}

function SignalsScene({ v, stage, total, L, label }: SceneProps) {
  const g = SIGNAL_GEO[v.name];
  const id = useArtId(`walk-signals-${v.name}`);
  const fmt = (p: number) => p.toLocaleString("en-US");
  const c = lane(
    g.win,
    v,
    [L.targetN(2), L.entryN(3), L.stopLoss],
    SIGNAL_PRICES.map(fmt)
  );
  const line = useMemo(
    () =>
      keyed(
        SIGNAL_KEYS.map(
          ([u, p]): Pt => [
            bandX(c.lineX, c.x2, u),
            r2(g.top + signalRow(p) * g.rowH),
          ]
        )
      ),
    [g, c.lineX, c.x2]
  );
  const price = useStepPrice(line.pts, line.at(SIGNAL_STOPS[stage] ?? 3));
  const isFilled = (i: number) =>
    i === 1 ? stage === 4 : i === 2 ? stage >= 2 : i === 3 && stage >= 3;
  const levels: Level[] = SIGNAL_PRICES.map((p, i) => {
    const filled = isFilled(i);
    return {
      label: i < 2 ? L.targetN(2 - i) : i < 5 ? L.entryN(i - 1) : L.stopLoss,
      price: fmt(p),
      y: r2(g.top + i * g.rowH),
      t: i < 2 ? "down" : i === 5 ? "gold" : "up",
      state: filled ? "filled" : i === 5 ? "active" : "pending",
      status: filled ? L.filled : i === 5 ? L.orderActive : L.pending,
    };
  });
  const freshRow = [-1, -1, 2, 3, 1][stage];
  const fills = [
    { k: 2, t: "up" as Tone, from: 2 },
    { k: 3, t: "up" as Tone, from: 3 },
    { k: 5, t: "down" as Tone, from: 4 },
  ].filter((fl) => stage >= fl.from);
  const toasts: (ToastSpec | undefined)[] = [
    undefined,
    { icon: Radio, t: "accent", title: L.signalReceived },
    { icon: ArrowDownRight, t: "up", title: L.firstBuyFilled },
    { icon: ArrowDownRight, t: "up", title: L.secondBuyFilled },
    { icon: CircleCheck, t: "up", title: L.takeProfitFilled },
  ];
  const orders = (
    <g>
      {levels.map((l, i) => (
        <LevelRow key={l.label} l={l} c={c} v={v} fresh={i === freshRow} />
      ))}
    </g>
  );
  const linkX = g.card.x + g.card.w / 2;

  return (
    <Art id={id} w={g.w} h={g.h} label={label}>
      <SignalCard id={id} b={g.card} v={v} L={L} stage={stage} />
      <line
        x1={linkX}
        x2={linkX}
        y1={g.card.y + g.card.h}
        y2={g.win.y}
        stroke={stage > 0 ? tone("accent") : "var(--viz-accent-line)"}
        strokeWidth={1.8}
        strokeDasharray="3 3"
      />
      <Frame
        id={id}
        b={g.win}
        v={v}
        title={L.signalsBot}
        aside={eventLabel(L, stage, total)}
      >
        {price.line}
        {/* The signal's orders appear once the bot receives it. */}
        {stage === 0 ? (
          <g opacity={0.45}>{orders}</g>
        ) : stage === 1 ? (
          <Appear key="placed" after={0.2}>
            {orders}
          </Appear>
        ) : (
          orders
        )}
        {fills.map((fl) => (
          <Fill
            key={fl.k}
            at={line.point[fl.k]}
            t={fl.t}
            fresh={fl.from === stage}
          />
        ))}
        {price.head}
      </Frame>
      <BriefToast
        id={id}
        spec={toasts[stage]}
        stage={stage}
        win={g.win}
        v={v}
      />
    </Art>
  );
}

/* ------------------------------------------------------------------------ */
/* Grid                                                                     */
/* ------------------------------------------------------------------------ */

const GRID_LEVELS = [105.8, 103.4, 101, 99, 96.6, 94.2];
const GRID_TOP = 105.8;
const GRID_SPAN = 11.6;
// Events end on keys 2 (buy), 4 (sell) and 6 (buy again); key 1 ends the
// sample. Each turn is one small, deliberate extreme.
const GRID_KEYS: [number, number][] = [
  [0, 100.2],
  [0.15, 100.7],
  [0.38, 99],
  [0.5, 98.5],
  [0.7, 101],
  [0.82, 101.6],
  [0.98, 99],
];
const GRID_STOPS = [1, 2, 4, 6];

type PlotGeo = { w: number; h: number; win: Box; top: number; bottom: number };
const GRID_GEO: Record<"w" | "c", PlotGeo> = {
  w: {
    w: 560,
    h: 350,
    win: { x: 10, y: 8, w: 540, h: 332 },
    top: 68,
    bottom: 284,
  },
  c: {
    w: 360,
    h: 232,
    win: { x: 4, y: 4, w: 352, h: 222 },
    top: 44,
    bottom: 176,
  },
};

function GridScene({ v, stage, total, L, label }: SceneProps) {
  const g = GRID_GEO[v.name];
  const id = useArtId(`walk-grid-${v.name}`);
  const fmt = (p: number) => p.toLocaleString("en-US");
  // The prices say buy (green, below) or sell (red, above): no repeated words.
  const c = lane(g.win, v, [], GRID_LEVELS.map(fmt));
  const Y = (p: number) =>
    r2(g.top + ((GRID_TOP - p) / GRID_SPAN) * (g.bottom - g.top));
  const line = useMemo(
    () =>
      keyed(
        GRID_KEYS.map(
          ([u, p]): Pt => [
            bandX(c.lineX, c.x2, u),
            r2(g.top + ((GRID_TOP - p) / GRID_SPAN) * (g.bottom - g.top)),
          ]
        )
      ),
    [g, c.lineX, c.x2]
  );
  const price = useStepPrice(line.pts, line.at(GRID_STOPS[stage] ?? 2));
  const filled = (p: number) => (p === 99 ? stage > 0 : p === 101 && stage > 1);
  const levels: Level[] = GRID_LEVELS.map((p) => ({
    label: "",
    name: p > 100 ? L.sellOrder : L.buyOrder,
    price: fmt(p),
    y: Y(p),
    t: p > 100 ? "down" : "up",
    state: filled(p) ? "filled" : "pending",
    status: filled(p) ? L.filled : L.pending,
  }));
  const fills = [
    { k: 2, t: "up" as Tone },
    { k: 4, t: "down" as Tone },
    { k: 6, t: "up" as Tone },
  ].slice(0, stage);
  const toasts: (ToastSpec | undefined)[] = [
    undefined,
    { icon: ArrowDownRight, t: "up", title: L.buyFilled },
    { icon: ArrowUpRight, t: "down", title: L.sellFilled },
    { icon: ArrowDownRight, t: "up", title: L.buyFilledAgain },
  ];

  return (
    <Art id={id} w={g.w} h={g.h} label={label}>
      <Frame
        id={id}
        b={g.win}
        v={v}
        title={`${L.gridBot} · DEMO/USDT`}
        aside={eventLabel(L, stage, total)}
      >
        {price.line}
        {levels.map((l, i) => (
          <LevelRow
            key={l.price}
            l={l}
            c={c}
            v={v}
            fresh={
              (GRID_LEVELS[i] === 99 && stage === 1) ||
              (GRID_LEVELS[i] === 101 && stage === 2)
            }
          />
        ))}
        {fills.map((fl, i) => (
          <Fill
            key={fl.k}
            at={line.point[fl.k]}
            t={fl.t}
            fresh={i === stage - 1}
          />
        ))}
        {price.head}
      </Frame>
      <BriefToast
        id={id}
        spec={toasts[stage]}
        stage={stage}
        win={g.win}
        v={v}
      />
    </Art>
  );
}

/* ------------------------------------------------------------------------ */
/* DCA                                                                      */
/* ------------------------------------------------------------------------ */

const DCA_ENTRIES = [60000, 58000, 56000, 54000];
const DCA_HI = 61600;
const DCA_LO = 53600;
const DCA_TARGETS = [0, 61200, 60180, 59160, 59160];
// Events end on keys 2, 3, 4 (entries 1-3) and 6 (take-profit); key 1 ends the
// sample.
const DCA_KEYS: [number, number][] = [
  [0, 61000],
  [0.16, 61200],
  [0.36, 60000],
  [0.53, 58000],
  [0.7, 56000],
  [0.8, 55600],
  [0.98, 59160],
];
const DCA_STOPS = [1, 2, 3, 4, 6];
const DCA_GEO: Record<"w" | "c", PlotGeo> = {
  w: { ...GRID_GEO.w, top: 64, bottom: 284 },
  c: { ...GRID_GEO.c, top: 40, bottom: 176 },
};

function DcaScene({ v, stage, total, L, label }: SceneProps) {
  const g = DCA_GEO[v.name];
  const id = useArtId(`walk-dca-${v.name}`);
  const fmt = (p: number) => p.toLocaleString("en-US");
  const c = lane(g.win, v, [L.entryN(4)], DCA_ENTRIES.map(fmt));
  const Y = (p: number) =>
    r2(g.top + ((DCA_HI - p) / (DCA_HI - DCA_LO)) * (g.bottom - g.top));
  const line = useMemo(
    () =>
      keyed(
        DCA_KEYS.map(
          ([u, p]): Pt => [
            bandX(c.lineX, c.x2, u),
            r2(g.top + ((DCA_HI - p) / (DCA_HI - DCA_LO)) * (g.bottom - g.top)),
          ]
        )
      ),
    [g, c.lineX, c.x2]
  );
  const price = useStepPrice(line.pts, line.at(DCA_STOPS[stage] ?? 2));
  const count = Math.min(stage, 3);
  const levels: Level[] = DCA_ENTRIES.map((p, i) => ({
    label: L.entryN(i + 1),
    price: fmt(p),
    y: Y(p),
    t: "up",
    state: count > i ? "filled" : "pending",
    status: count > i ? L.filled : L.pending,
  }));
  const target = DCA_TARGETS[stage];
  const targetText = `${L.targetN(1)} · ${fmt(target)}${
    stage === 4 ? " ✓" : ""
  }`;
  const fills = [
    { k: 2, t: "up" as Tone, from: 1 },
    { k: 3, t: "up" as Tone, from: 2 },
    { k: 4, t: "up" as Tone, from: 3 },
    { k: 6, t: "down" as Tone, from: 4 },
  ].filter((fl) => stage >= fl.from);
  const toasts: (ToastSpec | undefined)[] = [
    undefined,
    { icon: ArrowDownRight, t: "up", title: L.firstBuyFilled },
    { icon: ArrowDownRight, t: "up", title: L.secondBuyFilled },
    { icon: ArrowDownRight, t: "up", title: L.thirdBuyFilled },
    { icon: CircleCheck, t: "up", title: L.takeProfitFilled },
  ];

  return (
    <Art id={id} w={g.w} h={g.h} label={label}>
      <Frame
        id={id}
        b={g.win}
        v={v}
        title={`${L.dcaBot} · BTC/USDT`}
        aside={eventLabel(L, stage, total)}
      >
        {price.line}
        {/* The take-profit glides down with the average (never jumps); its
            label sits at the plot's right end, clear of the lane. */}
        {target ? (
          <Appear key="target" after={stage === 1 ? ARRIVE : 0}>
            <g
              className="ak-glide"
              style={{
                transform: `translate(0px, ${Y(target)}px)`,
                ...delay(ARRIVE * 0.7),
              }}
            >
              <line
                x1={c.lineX}
                x2={c.x2}
                y1={0}
                y2={0}
                stroke={tone("down")}
                strokeWidth={1.5}
                strokeDasharray="6 4"
              />
              <text
                x={c.x2}
                y={-7}
                textAnchor="end"
                fill={tone("down")}
                className={TXT.num}
                style={font(v.label, 600)}
              >
                {targetText}
              </text>
            </g>
          </Appear>
        ) : null}
        {levels.map((l, i) => (
          <LevelRow key={l.label} l={l} c={c} v={v} fresh={stage === i + 1} />
        ))}
        {fills.map((fl) => (
          <Fill
            key={fl.k}
            at={line.point[fl.k]}
            t={fl.t}
            fresh={fl.from === stage}
          />
        ))}
        {price.head}
      </Frame>
      <BriefToast
        id={id}
        spec={toasts[stage]}
        stage={stage}
        win={g.win}
        v={v}
      />
    </Art>
  );
}

/* ------------------------------------------------------------------------ */
/* TradingView                                                              */
/* ------------------------------------------------------------------------ */

// Unit coordinates in the plot. Events end on keys 1 (sample), 2 (alert, the
// crossover), 3 (entry filled) and 4 (exit).
const TV_KEYS: Pt[] = [
  [0, 0.3],
  [0.34, 0.7],
  [0.5, 0.51],
  [0.7, 0.36],
  [0.94, 0.18],
];
const TV_MA: Pt[] = [
  [0, 0.34],
  [0.2, 0.44],
  [0.4, 0.52],
  [0.6, 0.5],
  [0.8, 0.42],
  [1, 0.34],
];
const TV_STOPS = [1, 2, 3, 4];
const inPlot =
  (plot: Box) =>
  (p: Pt): Pt =>
    [r2(plot.x + p[0] * plot.w), r2(plot.y + p[1] * plot.h)];

type TvGeo = {
  w: number;
  h: number;
  card: Box;
  head: number;
  plot: Box;
  bell: number;
  win: Box;
};
const TV_GEO: Record<"w" | "c", TvGeo> = {
  w: {
    w: 560,
    h: 356,
    card: { x: 10, y: 6, w: 540, h: 184 },
    head: 36,
    plot: { x: 28, y: 96, w: 504, h: 80 },
    bell: 1,
    win: { x: 10, y: 212, w: 540, h: 134 },
  },
  c: {
    w: 360,
    h: 250,
    card: { x: 4, y: 4, w: 352, h: 124 },
    head: 28,
    plot: { x: 16, y: 74, w: 328, h: 44 },
    bell: 0.78,
    win: { x: 4, y: 142, w: 352, h: 100 },
  },
};

function TradingViewScene({ v, stage, total, L, label }: SceneProps) {
  const g = TV_GEO[v.name];
  const id = useArtId(`walk-tv-${v.name}`);
  const wide = v.name === "w";
  const { plot, card, win } = g;
  const line = useMemo(() => keyed(TV_KEYS.map(inPlot(plot))), [plot]);
  const ma = useMemo(() => smoothPoints(TV_MA.map(inPlot(plot)), 10), [plot]);
  const price = useStepPrice(line.pts, line.at(TV_STOPS[stage] ?? 3));
  const alert = line.point[2];
  const exit = line.point[4];
  const alertW = Math.round(L.entryAlert.length * 6.4 + 18);
  const steps = [
    {
      label: L.entryAlert,
      state: (stage >= 1 ? "filled" : "pending") as MarkState,
      status: stage >= 1 ? L.received : L.pending,
      t: "gold" as Tone,
      value: "",
    },
    {
      label: L.entryFilled,
      state: (stage >= 2 ? "filled" : "pending") as MarkState,
      status: stage >= 2 ? L.filled : L.pending,
      t: "up" as Tone,
      value: stage >= 2 && wide ? "64,420" : "",
    },
    {
      label: L.positionClosed,
      state: (stage >= 3 ? "filled" : "pending") as MarkState,
      status: stage >= 3 ? L.filled : L.pending,
      t: "down" as Tone,
      value: stage >= 3 && wide ? "+3.0%" : "",
    },
  ];
  const toasts: (ToastSpec | undefined)[] = [
    undefined,
    { icon: BellRing, t: "gold", title: L.entryAlert },
    { icon: CircleCheck, t: "up", title: L.entryFilled },
    { icon: CircleCheck, t: "up", title: L.positionClosed },
  ];
  const s = g.bell;
  const flag = (at: Pt, t: Tone, fresh: boolean) => (
    <g className={fresh ? "ak-rise" : undefined} style={delay(ARRIVE)}>
      <line
        x1={at[0]}
        x2={at[0]}
        y1={at[1] - 7}
        y2={at[1] - 22 * s}
        stroke={tone(t)}
        strokeWidth={1.8}
      />
      <circle
        cx={at[0]}
        cy={at[1]}
        r={5}
        fill="var(--viz-card)"
        stroke={tone(t)}
        strokeWidth={2.2}
      />
      <rect
        x={at[0] - 13 * s}
        y={at[1] - 48 * s}
        width={26 * s}
        height={26 * s}
        rx={8 * s}
        fill="var(--viz-card)"
      />
      <rect
        x={at[0] - 13 * s}
        y={at[1] - 48 * s}
        width={26 * s}
        height={26 * s}
        rx={8 * s}
        fill={soft(t)}
        stroke={tone(t)}
        strokeWidth={1.4}
      />
      <BellRing
        x={at[0] - 7 * s}
        y={at[1] - 42 * s}
        width={14 * s}
        height={14 * s}
        color={tone(t)}
        strokeWidth={2}
      />
    </g>
  );
  const colW = (win.w - 24) / 3;
  // The status row sits midway between the title bar and the toast's room.
  const rowY = win.y + v.bar + (win.h - v.bar - toastRoom(v)) / 2;
  const linkX = card.x + card.w / 2;

  return (
    <Art id={id} w={g.w} h={g.h} label={label}>
      {/* The TradingView chart and its alert condition */}
      <Card
        id={id}
        x={card.x}
        y={card.y}
        w={card.w}
        h={card.h}
        r={wide ? 16 : 12}
      >
        <text
          x={card.x + (wide ? 18 : 12)}
          y={card.y + g.head / 2 + 4.5}
          className={TXT.ink}
          style={font(wide ? 13 : 11.5, 600)}
        >
          TradingView
        </text>
        <text
          x={card.x + (wide ? 108 : 90)}
          y={card.y + g.head / 2 + 4}
          className={TXT.ink3}
          style={font(wide ? 11 : 10, 400)}
        >
          BTCUSDT · 1h
        </text>
        <line
          x1={card.x}
          x2={card.x + card.w}
          y1={card.y + g.head}
          y2={card.y + g.head}
          stroke="var(--viz-card-line)"
        />
        {[0.25, 0.5, 0.75].map((u) => (
          <line
            key={u}
            x1={plot.x}
            x2={plot.x + plot.w}
            y1={plot.y + u * plot.h}
            y2={plot.y + u * plot.h}
            stroke="var(--viz-rule-soft)"
          />
        ))}
        <path
          d={linePath(ma)}
          fill="none"
          stroke={tone("gold")}
          strokeWidth={1.6}
          strokeDasharray="5 4"
          strokeLinecap="round"
        />
        {price.line}
        {stage >= 1 ? flag(alert, "gold", stage === 1) : null}
        {stage >= 3 ? flag(exit, "down", stage === 3) : null}
        {stage >= 2 ? (
          <Fill at={line.point[3]} t="up" fresh={stage === 2} />
        ) : null}
        {price.head}
        {stage >= 1 ? (
          <Appear key="alert-chip" after={ARRIVE}>
            <Chip
              x={card.x + card.w - (g.head / 2 - 10) - alertW / 2}
              y={card.y + g.head / 2}
              text={L.entryAlert}
              t="gold"
              w={alertW}
              sm
            />
          </Appear>
        ) : null}
      </Card>

      {/* Webhook to the bot */}
      <line
        x1={linkX}
        x2={linkX}
        y1={card.y + card.h}
        y2={win.y}
        stroke={stage > 0 ? tone("accent") : "var(--viz-accent-line)"}
        strokeWidth={1.8}
        strokeDasharray="3 3"
      />
      <circle
        cx={linkX}
        cy={(card.y + card.h + win.y) / 2}
        r={wide ? 10 : 8}
        fill="var(--viz-card)"
        stroke="var(--viz-accent-line)"
      />
      <Webhook
        x={linkX - (wide ? 6 : 5)}
        y={(card.y + card.h + win.y) / 2 - (wide ? 6 : 5)}
        width={wide ? 12 : 10}
        height={wide ? 12 : 10}
        color={tone("accent")}
        strokeWidth={2}
      />

      <Frame
        id={id}
        b={win}
        v={v}
        title={L.tradingViewBot}
        aside={eventLabel(L, stage, total)}
      >
        {steps.map((r, i) => {
          const x = win.x + 12 + i * colW;
          return (
            <g key={r.label}>
              {i ? (
                <line
                  x1={x - 6}
                  x2={x - 6}
                  y1={rowY - 12}
                  y2={rowY + 12}
                  stroke="var(--viz-rule-soft)"
                />
              ) : null}
              <text
                x={x + v.mark * 2 + 8}
                y={rowY + 4}
                className={r.state === "filled" ? TXT.ink : TXT.ink3}
                style={font(wide ? 12 : 10.5, r.state === "filled" ? 500 : 400)}
              >
                {r.label}
                {r.value ? (
                  <tspan className={`${TXT.ink3} ${TXT.num}`} dx={6}>
                    {r.value}
                  </tspan>
                ) : null}
              </text>
              {r.state === "filled" && stage === i + 1 ? (
                <g aria-hidden>
                  <Mark
                    x={x + v.mark + 2}
                    y={rowY}
                    r={v.mark}
                    t={r.t}
                    state="pending"
                    title=""
                  />
                </g>
              ) : null}
              <Mark
                key={r.state}
                x={x + v.mark + 2}
                y={rowY}
                r={v.mark}
                t={r.t}
                state={r.state}
                title={r.status}
                fresh={stage === i + 1}
              />
            </g>
          );
        })}
      </Frame>
      <BriefToast id={id} spec={toasts[stage]} stage={stage} win={win} v={v} />
    </Art>
  );
}

/* ------------------------------------------------------------------------ */
/* Hedging                                                                  */
/* ------------------------------------------------------------------------ */

const HEDGE_GEO: Record<"w" | "c", { w: number; h: number; win: Box }> = {
  w: { w: 560, h: 250, win: { x: 10, y: 8, w: 540, h: 234 } },
  c: { w: 360, h: 200, win: { x: 4, y: 4, w: 352, h: 192 } },
};

function HedgingScene({ v, stage, total, L, label }: SceneProps) {
  const g = HEDGE_GEO[v.name];
  const id = useArtId(`walk-hedge-${v.name}`);
  const wide = v.name === "w";
  const { win } = g;
  const sides = [
    { word: L.long, t: "up" as Tone, arrow: ArrowUpRight, change: "+1.8%" },
    {
      word: L.short,
      t: "down" as Tone,
      arrow: ArrowDownRight,
      change: "−1.8%",
    },
  ];
  const half = win.w / 2;
  const top = win.y + v.bar;
  const mid = top + (win.h - v.bar) / 2;
  return (
    <Art id={id} w={g.w} h={g.h} label={label}>
      <Frame
        id={id}
        b={win}
        v={v}
        title={`${L.hedgingBot} · BTC/USDT`}
        aside={eventLabel(L, stage, total)}
      >
        <line
          x1={win.x + half}
          x2={win.x + half}
          y1={top + 16}
          y2={win.y + win.h - 16}
          stroke="var(--viz-rule-soft)"
        />
        {sides.map((s, i) => {
          const cx = win.x + half * i + half / 2;
          const Arrow = s.arrow;
          return (
            <g key={s.word} opacity={stage > 0 ? 1 : 0.5}>
              <text
                x={cx}
                y={mid - (wide ? 30 : 24)}
                textAnchor="middle"
                fill={tone(s.t)}
                style={{
                  ...font(wide ? 11.5 : 10.5, 600),
                  letterSpacing: "0.08em",
                }}
              >
                {s.word.toUpperCase()}
              </text>
              <text
                x={cx}
                y={mid + (wide ? 12 : 8)}
                textAnchor="middle"
                className={`${TXT.ink} ${TXT.num}`}
                style={font(wide ? 30 : 24, 600)}
              >
                50%
              </text>
              {stage > 1 ? (
                <Appear key={`chg-${i}`} after={0.1}>
                  <Arrow
                    x={cx - (wide ? 30 : 26)}
                    y={mid + (wide ? 26 : 18)}
                    width={wide ? 16 : 14}
                    height={wide ? 16 : 14}
                    color={tone(s.t)}
                    strokeWidth={2.2}
                  />
                  <text
                    x={cx - (wide ? 10 : 8)}
                    y={mid + (wide ? 39 : 29)}
                    fill={tone(s.t)}
                    className={TXT.num}
                    style={font(wide ? 13 : 12, 600)}
                  >
                    {s.change}
                  </text>
                </Appear>
              ) : null}
            </g>
          );
        })}
      </Frame>
    </Art>
  );
}

/* ------------------------------------------------------------------------ */

const SCENES: Record<TradingIntent, ComponentType<SceneProps>> = {
  signals: SignalsScene,
  grid: GridScene,
  tradingview: TradingViewScene,
  hedging: HedgingScene,
  dca: DcaScene,
};

/** The walkthrough illustration for a method at one of its events. */
export default function WalkthroughArt({
  intent,
  stage,
  total,
  label,
}: {
  intent: TradingIntent;
  stage: number;
  total: number;
  /** What the current event shows, for assistive technology. */
  label: string;
}) {
  const L = useArtLabels();
  const Scene = SCENES[intent] ?? DcaScene;
  const common = { stage, total, L, label };
  return (
    <ArtSwitch
      min={440}
      wide={<Scene v={WIDE} {...common} />}
      narrow={<Scene v={COMPACT} {...common} />}
    />
  );
}
